// Mock data + contract for the inquiry template-settings dialog. The hooks in
// templateSettingsApi.ts return these shapes; swap the queryFn/mutationFn bodies
// for real apiClientV2 calls when the backend lands.

import { SAMPLE_LOGO_URL } from "../draft/inquiryDraftMock"

export interface TemplateLogo {
  url: string
  // fileName / meta are only known for a logo uploaded in this session — the
  // GET endpoint returns just the resolved url.
  fileName?: string
  meta?: string // e.g. "SVG · 412 × 96 · uploaded Mar 2026"
}

// One editable email cover-message (per stage × language). {{variables}} are filled
// per inquiry at send; the backend validates the token set and rejects the reserved
// "[Ref: …]" token (it's appended to the subject automatically).
export interface EmailCopy {
  subject: string
  body: string
}

// The org's editable email templates. `first` uses both subject + body; `reminder`
// uses body only — its subject is always the threaded "Re: <original>" the backend
// forces, so the reminder subject here is carried through untouched, never edited.
export interface EmailTemplatesByLang {
  first: { en: EmailCopy; de: EmailCopy }
  reminder: { en: EmailCopy; de: EmailCopy }
}

export interface InquiryTemplateSettings {
  logo: TemplateLogo | null
  // Footer printed at the bottom of the PDF questionnaire attachment.
  questionnaireFooter: string
  // Per-language sign-off appended to the end of the email body — edited in the Email configuration
  // section next to the subject/body, and shown read-only in the composer.
  emailSignatures: { en: string; de: string }
  // Org's inquiry contact person → the questionnaire PDF cover's "Ansprechpartner" / "Contact".
  contactPerson: string
  // Cc addresses seeded into every NEW inquiry draft (editable per draft in the composer).
  defaultCc: string[]
  // Org default attachment format for NEW drafts, and whether it is enforced (the
  // composer then hides the format choice). Replaces the MC_LQ_INQUIRY_PACKAGE flag.
  defaultAttachmentFormat: "pdf" | "excel" | "pdf_and_excel"
  attachmentFormatLocked: boolean
  // Auto-reminder cadence (honored by the backend reminder scheduler). Just two knobs:
  // how often to remind, and how many times before handing back.
  remindersEnabled: boolean
  reminderIntervalDays: number // days between reminders (first fires when the deadline lapses)
  maxReminders: number // stop after this many reminders → handed back for follow-up
  defaultDueDays: number // response window seeded into every new draft (today + N)
  // The org's editable email cover-message templates (first + reminder, EN/DE). The single
  // source of truth for the email content the composer pre-fills and the auto-reminder sends;
  // the cadence section reads its read-only reminder preview from here too.
  emailTemplates: EmailTemplatesByLang
  // ── Outgoing conformity declarations v2 (spec §6) ──
  declarationFreshnessDays: number // supplier verdicts older than this are stale
  // Scope guards (conformity v2 WP1): the editor warns above the first and the
  // API refuses generate above the second. Defaults 500 / 2000.
  scopeWarnSubjects: number
  scopeMaxSubjects: number
  printValidUntil: boolean // print "valid until" on the document (org default)
  validityDays: number // issued_at + N when printValidUntil is on
  // Signatory name/function moved to the org's signature library
  // (Settings → Signatures) — only the issuer place stays here.
  place: string // issuer place for the signature box
  //: Language of the org's Konformitätserklärungen AND its Abfrage emails.
  //: Seeded from the first user's language at onboarding; editable here because
  //: an org with no row silently defaults to English on its first settings save.
  defaultLanguage: string // 'de' | 'en'
  productGroupLabel: string // org label for mc_articles.product_group; "" = not configured
}

export const DEFAULT_TEMPLATE_SETTINGS: InquiryTemplateSettings = {
  logo: {
    url: SAMPLE_LOGO_URL,
    fileName: "HIK-logo.svg",
    meta: "SVG · 412 × 96 · uploaded Mar 2026",
  },
  questionnaireFooter: `HIK Industrieelektronik GmbH · Robert-Bosch-Str. 12, 70794 Filderstadt · materialcompliance@hik.de
Managing Director: Dr. M. Haas · Local court Stuttgart HRB 00000 · Sent automatically via turnus.ai`,
  emailSignatures: {
    en: `Kind regards
Ferdinand Heck
Material Compliance · HIK GmbH`,
    de: `Mit freundlichen Grüßen
Ferdinand Heck
Material Compliance · HIK GmbH`,
  },
  contactPerson: "Ferdinand Heck · Material Compliance · +49 711 000000",
  defaultCc: [],
  defaultAttachmentFormat: "pdf",
  attachmentFormatLocked: false,
  remindersEnabled: true,
  reminderIntervalDays: 7,
  maxReminders: 3,
  defaultDueDays: 30,
  declarationFreshnessDays: 365,
  scopeWarnSubjects: 500,
  scopeMaxSubjects: 2000,
  printValidUntil: false,
  validityDays: 365,
  place: "",
  defaultLanguage: "de",
  productGroupLabel: "",
  emailTemplates: {
    first: {
      en: {
        subject: "Material compliance request",
        body: `Dear {{supplier_name}},

Please complete the attached material-compliance request and return it by {{deadline}}.

Kind regards`,
      },
      de: {
        subject: "Materialauskunft",
        body: `Sehr geehrte Damen und Herren,

bitte füllen Sie die beigefügte Materialauskunft aus und senden Sie sie bis {{frist}} zurück.

Mit freundlichen Grüßen`,
      },
    },
    reminder: {
      en: {
        subject: "",
        body: `Dear {{supplier_name}},

We are still awaiting your response to our material-compliance request. Please return the completed form by {{deadline}}.

Kind regards`,
      },
      de: {
        subject: "",
        body: `Sehr geehrte Damen und Herren,

wir warten noch auf Ihre Antwort auf unsere Materialauskunft. Bitte senden Sie das ausgefüllte Formular bis {{frist}} zurück.

Mit freundlichen Grüßen`,
      },
    },
  },
}

// Static preview copy. Only the logo / footers are editable in this dialog; the
// rest is generic sample data so the preview pane looks realistic.
// The supplier-name placeholder is localized at its render sites
// (MC_TPL_PREVIEW_SUPPLIER_NAME) rather than fixed here — the {{supplier_name}}
// token it fills is shown in the UI language, not always English.
export const PREVIEW_TO_EMAIL = "compliance@supplier.example"
export const PREVIEW_REFERENCE = "QMS-Q-2026-0042"
